import { AlertCircle, CheckCheck, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerPackageById } from '../../../api/package.api';
import styles from '../../../assets/scss/pages/CustomerPackages.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

// Port từ app: screens/admin/CustomerPackageUsages.js (chuyển sang modal cho web).
// Lịch sử buổi đã dùng của 1 thẻ gói: thời gian, NV làm, doanh thu ghi nhận.

const formatVnd = (n) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export default function CustomerPackageUsages({ open, packageId, packageName, onClose }) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canViewBillDetail = hasPermission(PERMISSIONS.BILL.VIEW_DETAIL);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !packageId) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        getCustomerPackageById(packageId)
            .then((res) => {
                if (!cancelled) setData(res?.data?.data || null);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || err.message || 'Lỗi tải');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, packageId]);

    if (!open) return null;

    const usages = data?.usages || [];
    const snap = data?.templateSnapshot || {};
    const title = packageName || snap.name || data?.package?.name || 'Thẻ dịch vụ';

    const openBill = (billId) => {
        if (!billId || !canViewBillDetail) return;
        onClose();
        navigate(`/admin/bills/${billId}`);
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
                <div className={styles.sheetBody}>
                    <div className={styles.headerRow}>
                        <div style={{ flex: 1 }}>
                            <h3 className={styles.title}>Lịch sử dùng</h3>
                        </div>
                        <button className={styles.closeBtn} onClick={onClose}>
                            <X size={22} />
                        </button>
                    </div>

                    <div className={styles.summaryCard}>
                        <p className={styles.summaryTitle}>{title}</p>
                        <span className={styles.summarySub}>Tổng {usages.length} buổi đã dùng</span>
                    </div>

                    {loading ? (
                        <div className={styles.sheetCenter}>
                            <div className={styles.spinner} />
                            <span className={styles.muted}>Đang tải lịch sử dùng...</span>
                        </div>
                    ) : error ? (
                        <div className={styles.sheetCenter}>
                            <AlertCircle size={40} color="#ef4444" />
                            <span className={styles.muted} style={{ color: '#ef4444' }}>{error}</span>
                        </div>
                    ) : usages.length === 0 ? (
                        <div className={styles.sheetCenter}>
                            <CheckCheck size={48} color="#d1d5db" />
                            <span className={styles.emptyTitle}>Chưa có buổi nào hoàn thành</span>
                            <span className={styles.muted}>
                                Khi NV hoàn thành buổi, lịch sử sẽ hiển thị tại đây
                            </span>
                        </div>
                    ) : (
                        usages.map((u, index) => {
                            const canOpen = !!u.billId && canViewBillDetail;
                            return (
                                <div
                                    key={`usage-${u.id ?? index}`}
                                    className={`${styles.usageCard}${canOpen ? ` ${styles.usageCardClickable}` : ''}`}
                                    onClick={() => openBill(u.billId)}
                                >
                                    <div className={styles.sessionBadge}>
                                        <span className={styles.sessionNum}>Buổi {u.sessionNumber ?? index + 1}</span>
                                    </div>
                                    <div className={styles.usageInfo}>
                                        <div className={styles.usageTime}>{formatDateTime(u.usedAt)}</div>
                                        {u.employee?.name ? (
                                            <div className={styles.usageMeta}>NV: {u.employee.name}</div>
                                        ) : null}
                                        {Number(u.recognizedRevenue) > 0 ? (
                                            <div className={styles.usageMeta}>
                                                Ghi nhận: {formatVnd(u.recognizedRevenue)}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div
                                        className={styles.statusBadge}
                                        style={{ background: '#dcfce7', color: '#16a34a' }}
                                    >
                                        Đã dùng
                                    </div>
                                    {canOpen ? <ChevronRight size={18} color="#94a3b8" /> : null}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
